import React, { useState } from 'react';
import { Box, Collapse, IconButton, Skeleton, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { colors, typography } from '../../../../styles/theme';

export const PANEL_SECTION_TITLE_SX = {
  fontFamily: typography.fontFamily.primary,
  fontWeight: 600,
  fontSize: '16px',
  lineHeight: '16px',
  letterSpacing: '0.02em',
  color: colors.textPanelTitle,
  flexShrink: 0,
  textTransform: 'uppercase',
};

export const LOT_INFO_SECTION_BORDER = '#383838';
export const LOT_INFO_SECTION_HEADER_BG = '#242424';
export const LOT_INFO_SECTION_CONTENT_BG = '#1C1C1C';

export const LOT_INFO_DETAIL_SURFACE_SX = {
  borderRadius: '8px',
  border: `1px solid ${LOT_INFO_SECTION_BORDER}`,
  bgcolor: LOT_INFO_SECTION_CONTENT_BG,
  overflow: 'hidden',
};

export const lotInfoRowOuterSx = (hideDivider = false) => ({
  borderBottom: hideDivider ? 'none' : `1px solid ${LOT_INFO_SECTION_BORDER}`,
});

export const LOT_INFO_ROW_INNER_SX = {
  px: 2,
  py: 1,
};

export const LOT_INFO_FIELD_LABEL_SX = {
  fontFamily: typography.fontFamily.primary,
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '16px',
  letterSpacing: 0,
  color: 'rgba(255,255,255,0.88)',
};

export const lotInfoFieldLabelLayoutSx = (flexBasis = '46%') => ({
  flex: `0 0 ${flexBasis}`,
  minWidth: 0,
  ...LOT_INFO_FIELD_LABEL_SX,
});

export const lotInfoFieldValueSx = ({
  muted = false,
  highlight = false,
  noWrap = false,
  monospace = false,
} = {}) => ({
  fontFamily: monospace
    ? 'ui-monospace, SFMono-Regular, Menlo, monospace'
    : typography.fontFamily.primary,
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '16px',
  letterSpacing: 0,
  fontVariantNumeric: monospace ? 'tabular-nums' : undefined,
  color: muted
    ? 'rgba(255,255,255,0.28)'
    : (highlight ? 'rgba(255,255,255,0.92)' : 'rgba(255,255,255,0.78)'),
  whiteSpace: noWrap ? 'nowrap' : 'pre-wrap',
  wordBreak: noWrap ? 'normal' : 'break-word',
  overflow: noWrap ? 'hidden' : 'visible',
  textOverflow: noWrap ? 'ellipsis' : 'clip',
});

export const LOT_INFO_FIELD_ADORNMENT_SX = {
  ...lotInfoFieldValueSx(),
  color: 'rgba(255,255,255,0.35)',
};

export const LOT_INFO_FIELD_ROW_SX = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 1.5,
  ...LOT_INFO_ROW_INNER_SX,
};

export const LotInfoMetricIndicator = ({ color }) => (
  <Box
    component="span"
    sx={{
      width: 10,
      height: 10,
      borderRadius: '2px',
      bgcolor: color,
      flexShrink: 0,
    }}
  />
);

export const BLOCKED_FIELD_MASK_CHAR = '•';

const BLOCKED_FIELD_MASK_LENGTHS = {
  'Terrio UUID': 12,
  'Número Catastral': 11,
  Superficie: 7,
  Tipo: 9,
  Estado: 6,
  'Coordenadas (lat, lng)': 16,
  Valuación: 10,
};

export const isBlockedFieldMask = (value) => (
  typeof value === 'string'
  && value.length > 0
  && [...value].every((char) => char === BLOCKED_FIELD_MASK_CHAR)
);

/** Placeholder tipo contraseña para campos bloqueados (cuota o sesión). */
export const getBlockedFieldPlaceholder = (
  fieldKey,
  { maskSensitive = false, isQuotaExceeded = false } = {},
) => {
  if (maskSensitive || isQuotaExceeded) {
    const length = BLOCKED_FIELD_MASK_LENGTHS[fieldKey] ?? 8;
    return BLOCKED_FIELD_MASK_CHAR.repeat(length);
  }

  return 'Este dato no está disponible.';
};

export const PRIVATE_BLOCK_SX = {
  borderRadius: '8px',
  border: `1px dashed ${LOT_INFO_SECTION_BORDER}`,
  bgcolor: LOT_INFO_SECTION_CONTENT_BG,
};

export const PUBLICATION_BLOCK_SX = {
  borderRadius: '8px',
  border: `1px solid ${LOT_INFO_SECTION_BORDER}`,
  bgcolor: LOT_INFO_SECTION_CONTENT_BG,
};

export const ORGANIZE_BLOCK_SX = {
  borderRadius: '8px',
  border: `1px solid ${LOT_INFO_SECTION_BORDER}`,
  bgcolor: LOT_INFO_SECTION_CONTENT_BG,
};

const BLOCK_TITLE_SX = {
  fontSize: '14px',
  color: colors.textPanelTitle,
  textTransform: 'uppercase',
  letterSpacing: '0.02em',
  fontWeight: 600,
  fontFamily: typography.fontFamily.primary,
  lineHeight: '16px',
};

/** Título de subgrupo dentro de un bloque (Precio, Presentación, SUPERFICIES…). */
export const FORM_GROUP_TITLE_SX = {
  ...BLOCK_TITLE_SX,
  display: 'block',
  mb: 1.25,
};

const BLOCK_SUBTITLE_SX = {
  fontSize: '12px',
  color: '#ABABAB',
  mt: 0.35,
  lineHeight: 1.4,
  fontFamily: typography.fontFamily.primary,
};

/** Switch activo — mismo teal que UF / botones primarios. */
export const FORM_SWITCH_SX = {
  '& .MuiSwitch-switchBase.Mui-checked': { color: colors.primaryLight },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: colors.primary },
  '& .MuiSwitch-switchBase.Mui-disabled': { color: 'rgba(255,255,255,0.35)' },
  '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled': { color: 'rgba(72,146,145,0.55)' },
  '& .MuiSwitch-switchBase.Mui-checked.Mui-disabled + .MuiSwitch-track': {
    bgcolor: 'rgba(72,146,145,0.35)',
  },
  '& .MuiSwitch-track': { bgcolor: 'rgba(255,255,255,0.18)' },
};

/** Botón secundario del formulario (Cancelar / Volver) — borde blanco, fondo transparente. */
export const FORM_SECONDARY_BUTTON_SX = {
  textTransform: 'none',
  borderRadius: '8px',
  minHeight: 40,
  height: 40,
  px: 2,
  fontSize: '14px',
  fontWeight: typography.fontWeight.medium,
  fontFamily: typography.fontFamily.primary,
  color: '#E8F4F4',
  border: '1px solid #E8F4F4',
  bgcolor: 'transparent',
  boxShadow: 'none',
  '&:hover': {
    bgcolor: 'rgba(232,244,244,0.06)',
    borderColor: '#E8F4F4',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    color: 'rgba(232,244,244,0.35)',
    borderColor: 'rgba(232,244,244,0.25)',
  },
};

/** Botón primario del formulario (Guardar / Continuar) — teal sólido. */
export const FORM_PRIMARY_BUTTON_SX = {
  textTransform: 'none',
  borderRadius: '8px',
  minHeight: 40,
  height: 40,
  px: 2.25,
  fontSize: '14px',
  fontWeight: typography.fontWeight.semibold,
  fontFamily: typography.fontFamily.primary,
  color: '#FFFFFF',
  bgcolor: colors.primary,
  boxShadow: 'none',
  '&:hover': {
    bgcolor: colors.primaryHover,
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    bgcolor: 'rgba(72,146,145,0.35)',
    color: 'rgba(255,255,255,0.55)',
  },
  '& .MuiButton-startIcon': {
    marginRight: '8px',
  },
};

export const FIELD_HINT_SX = {
  display: 'block',
  fontSize: '11px',
  color: 'rgba(255,255,255,0.38)',
  mt: 0.5,
  lineHeight: 1.4,
};

/** IconButton cuadrado de toolbar — alineado con listAddButtonSx (Mis listas / Registros). */
export const SECTION_BAR_ICON_BUTTON_SX = {
  width: 28,
  height: 28,
  minWidth: 28,
  flexShrink: 0,
  p: 0,
  borderRadius: '4px',
  border: '1px solid #4a4a4a',
  // bgcolor: '#141414',
  color: '#e8f4f4',
  boxSizing: 'border-box',
  '&:hover': {
    bgcolor: 'rgba(255,255,255,0.06)',
    borderColor: '#e8f4f4',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 16,
    color: '#e8f4f4',
  },
  '&.Mui-disabled': {
    opacity: 0.5,
    borderColor: 'rgba(255,255,255,0.12)',
  },
};

export const SECTION_BAR_ICON_BUTTON_DELETE_SX = {
  ...SECTION_BAR_ICON_BUTTON_SX,
  color: 'rgba(248,113,113,0.95)',
  border: '1px solid rgba(248,113,113,0.25)',
  '&:hover': {
    bgcolor: 'rgba(248,113,113,0.10)',
    borderColor: 'rgba(248,113,113,0.55)',
  },
  '& .MuiSvgIcon-root': {
    fontSize: 16,
    color: 'rgba(248,113,113,0.95)',
  },
};

export const BlockHeader = ({ icon, title, subtitle, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0, flex: 1 }}>
      {icon ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '16px',
            flexShrink: 0,
            '& .MuiSvgIcon-root': { display: 'block' },
          }}
        >
          {icon}
        </Box>
      ) : null}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={BLOCK_TITLE_SX}>{title}</Typography>
        {subtitle ? <Typography sx={BLOCK_SUBTITLE_SX}>{subtitle}</Typography> : null}
      </Box>
    </Box>
    {action}
  </Box>
);

export const BlockCollapseToggle = ({ expanded, onToggle, labelExpanded, labelCollapsed }) => (
  <IconButton
    size="small"
    onClick={(event) => {
      event.stopPropagation();
      onToggle();
    }}
    aria-label={expanded ? labelExpanded : labelCollapsed}
    sx={{
      color: '#ABABAB',
      '&:hover': { color: '#E8F4F4', bgcolor: 'rgba(255,255,255,0.06)' },
    }}
  >
    <ExpandMoreIcon
      sx={{
        fontSize: 20,
        color: 'inherit',
        transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
        transition: 'transform 0.2s ease',
      }}
    />
  </IconButton>
);

export const LotInfoCollapseSection = ({
  title,
  titleAdornment = null,
  defaultExpanded = true,
  collapsible = true,
  showCollapseToggle = true,
  actions = null,
  children,
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isExpanded = collapsible ? expanded : true;
  const hasBody = children != null && children !== false;

  const toggleExpanded = () => {
    if (!collapsible) return;
    setExpanded((prev) => !prev);
  };

  return (
    <Box
      sx={{
        mx: 2,
        mb: 1.5,
        border: `1px solid ${LOT_INFO_SECTION_BORDER}`,
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2,
          py: 1.5,
          bgcolor: LOT_INFO_SECTION_HEADER_BG,
          borderBottom: isExpanded && hasBody ? `1px solid ${LOT_INFO_SECTION_BORDER}` : 'none',
          cursor: collapsible ? 'pointer' : 'default',
          userSelect: 'none',
        }}
        onClick={collapsible ? toggleExpanded : undefined}
        onKeyDown={collapsible ? (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggleExpanded();
          }
        } : undefined}
        role={collapsible ? 'button' : undefined}
        tabIndex={collapsible ? 0 : undefined}
        aria-expanded={collapsible ? isExpanded : undefined}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <Typography sx={PANEL_SECTION_TITLE_SX}>{title}</Typography>
          {titleAdornment}
        </Box>
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}
          onClick={(event) => event.stopPropagation()}
        >
          {actions}
          {showCollapseToggle && collapsible && (
            <BlockCollapseToggle
              expanded={isExpanded}
              onToggle={toggleExpanded}
              labelExpanded={`Contraer ${title}`}
              labelCollapsed={`Expandir ${title}`}
            />
          )}
        </Box>
      </Box>
      {hasBody && (
        <Collapse in={isExpanded}>
          <Box sx={{ bgcolor: LOT_INFO_SECTION_CONTENT_BG }}>
            {children}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const LOT_USER_CONTENT_SKELETON_FIELD_SX = {
  bgcolor: 'rgba(255,255,255,0.06)',
  borderRadius: 1,
};

const LOT_USER_CONTENT_SKELETON_LINE_PRIMARY_SX = {
  bgcolor: 'rgba(255,255,255,0.10)',
};

const LOT_USER_CONTENT_SKELETON_LINE_MUTED_SX = {
  bgcolor: 'rgba(255,255,255,0.06)',
};

const LotUserContentFieldSkeleton = ({ multiline = false }) => (
  <Box>
    <Skeleton
      variant="text"
      width={96}
      height={10}
      sx={LOT_USER_CONTENT_SKELETON_LINE_MUTED_SX}
    />
    <Skeleton
      variant="rounded"
      width="100%"
      height={multiline ? 48 : 16}
      sx={{ ...LOT_USER_CONTENT_SKELETON_FIELD_SX, mt: 0.5 }}
    />
  </Box>
);

const LotUserContentBlockHeaderSkeleton = ({ subtitleWidth = '85%', showToggle = true }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flex: 1, minWidth: 0 }}>
      <Skeleton
        variant="circular"
        width={16}
        height={16}
        sx={LOT_USER_CONTENT_SKELETON_LINE_MUTED_SX}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Skeleton
          variant="text"
          width={72}
          height={12}
          sx={LOT_USER_CONTENT_SKELETON_LINE_PRIMARY_SX}
        />
        <Skeleton
          variant="text"
          width={subtitleWidth}
          height={10}
          sx={{ ...LOT_USER_CONTENT_SKELETON_LINE_MUTED_SX, mt: 0.5 }}
        />
      </Box>
    </Box>
    {showToggle && (
      <Skeleton
        variant="circular"
        width={18}
        height={18}
        sx={LOT_USER_CONTENT_SKELETON_LINE_MUTED_SX}
      />
    )}
  </Box>
);

const LotUserContentSubsectionSkeleton = ({ titleWidth = 88, valueHeight = 16 }) => (
  <Box>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
      <Skeleton
        variant="text"
        width={titleWidth}
        height={16}
        sx={LOT_USER_CONTENT_SKELETON_LINE_PRIMARY_SX}
      />
      <Skeleton
        variant="rounded"
        width={72}
        height={22}
        sx={LOT_USER_CONTENT_SKELETON_FIELD_SX}
      />
    </Box>
    <Skeleton
      variant="rounded"
      width="100%"
      height={valueHeight}
      sx={{ ...LOT_USER_CONTENT_SKELETON_FIELD_SX, borderRadius: '8px' }}
    />
  </Box>
);

/** Placeholder visual mientras carga la info privada del lote (vista / formulario). */
export const LotUserPrivateInfoSkeleton = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2 }}>
    <Box sx={{ ...PRIVATE_BLOCK_SX, p: 1.5 }}>
      <LotUserContentBlockHeaderSkeleton subtitleWidth="78%" />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.25 }}>
        <LotUserContentFieldSkeleton />
        <LotUserContentFieldSkeleton multiline />
      </Box>
    </Box>

    <Box sx={{ ...PUBLICATION_BLOCK_SX, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <LotUserContentBlockHeaderSkeleton subtitleWidth="68%" showToggle={false} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
            <Skeleton variant="text" width={110} height={16} sx={LOT_USER_CONTENT_SKELETON_LINE_PRIMARY_SX} />
            <Skeleton variant="rounded" width={72} height={22} sx={LOT_USER_CONTENT_SKELETON_FIELD_SX} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <LotUserContentFieldSkeleton />
            <LotUserContentFieldSkeleton multiline />
          </Box>
        </Box>
        <LotUserContentSubsectionSkeleton titleWidth={140} />
        <Box sx={{ borderTop: `1px solid ${LOT_INFO_SECTION_BORDER}`, pt: 1.25 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.25 }}>
            <Skeleton variant="text" width={56} height={16} sx={LOT_USER_CONTENT_SKELETON_LINE_PRIMARY_SX} />
            <Skeleton variant="rounded" width={72} height={22} sx={LOT_USER_CONTENT_SKELETON_FIELD_SX} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[0, 1, 2].map((index) => (
              <Skeleton
                key={index}
                variant="rounded"
                width={72}
                height={72}
                sx={{ ...LOT_USER_CONTENT_SKELETON_FIELD_SX, borderRadius: '8px' }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>

    <Box sx={{ ...ORGANIZE_BLOCK_SX, p: 1.5 }}>
      <LotUserContentBlockHeaderSkeleton subtitleWidth="62%" />
      <Skeleton
        variant="rounded"
        width="100%"
        height={40}
        sx={{ ...LOT_USER_CONTENT_SKELETON_FIELD_SX, mt: 1.25, borderRadius: '8px' }}
      />
    </Box>
  </Box>
);

const LotInfoCollapseSectionSkeleton = ({
  titleWidth = 160,
  showToggle = true,
  showAction = false,
  children = null,
}) => (
  <Box
    sx={{
      mx: 2,
      mb: 1.5,
      border: `1px solid ${LOT_INFO_SECTION_BORDER}`,
      borderRadius: '8px',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        px: 2,
        py: 1.5,
        bgcolor: LOT_INFO_SECTION_HEADER_BG,
        borderBottom: children ? `1px solid ${LOT_INFO_SECTION_BORDER}` : 'none',
      }}
    >
      <Skeleton
        variant="rounded"
        width={titleWidth}
        height={16}
        sx={{ bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 0.5 }}
      />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {showAction && (
          <Skeleton
            variant="rounded"
            width={28}
            height={28}
            sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: '4px' }}
          />
        )}
        {showToggle && (
          <Skeleton
            variant="circular"
            width={20}
            height={20}
            sx={{ bgcolor: 'rgba(255,255,255,0.08)' }}
          />
        )}
      </Box>
    </Box>
    {children && (
      <Box sx={{ bgcolor: LOT_INFO_SECTION_CONTENT_BG }}>
        {children}
      </Box>
    )}
  </Box>
);

const LotInfoRowSkeleton = ({
  labelWidth = '42%',
  valueWidth = '28%',
  hideDivider = false,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      px: 2,
      py: 1,
      borderBottom: hideDivider ? 'none' : `1px solid ${LOT_INFO_SECTION_BORDER}`,
    }}
  >
    <Skeleton
      variant="text"
      width={labelWidth}
      height={16}
      sx={{ bgcolor: 'rgba(255,255,255,0.12)', flexShrink: 0 }}
    />
    <Box sx={{ flex: 1, minWidth: 0 }} />
    <Skeleton
      variant="text"
      width={valueWidth}
      height={16}
      sx={{ bgcolor: 'rgba(255,255,255,0.08)', flexShrink: 0 }}
    />
  </Box>
);

/** Skeleton del tab Lote alineado a LotInfoCollapseSection. */
export const LotInfoPanelSkeleton = () => (
  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', pt: 2, pb: 1 }}>
    <LotInfoCollapseSectionSkeleton
      titleWidth={168}
      showToggle={false}
      showAction
    />

    <LotInfoCollapseSectionSkeleton titleWidth={180}>
      {[
        { labelWidth: '34%', valueWidth: '52%' },
        { labelWidth: '48%', valueWidth: '34%' },
        { labelWidth: '38%', valueWidth: '22%' },
        { labelWidth: '28%', valueWidth: '30%' },
        { labelWidth: '32%', valueWidth: '26%' },
        { labelWidth: '40%', valueWidth: '36%' },
        { labelWidth: '42%', valueWidth: '38%' },
        { labelWidth: '34%', valueWidth: '44%' },
        { labelWidth: '30%', valueWidth: '48%' },
      ].map((row, index, arr) => (
        <LotInfoRowSkeleton
          key={index}
          {...row}
          hideDivider={index === arr.length - 1}
        />
      ))}
    </LotInfoCollapseSectionSkeleton>

    <LotInfoCollapseSectionSkeleton titleWidth={110}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        <Skeleton
          variant="rounded"
          width="100%"
          height={168}
          sx={{
            bgcolor: 'rgba(255,255,255,0.05)',
            borderRadius: '8px',
            border: `1px solid ${LOT_INFO_SECTION_BORDER}`,
          }}
        />
        <Box
          sx={{
            borderRadius: '8px',
            border: `1px solid ${LOT_INFO_SECTION_BORDER}`,
            overflow: 'hidden',
          }}
        >
          <LotInfoRowSkeleton labelWidth="42%" valueWidth="24%" />
          <LotInfoRowSkeleton labelWidth="38%" valueWidth="28%" hideDivider />
        </Box>
      </Box>
    </LotInfoCollapseSectionSkeleton>

    <LotInfoCollapseSectionSkeleton titleWidth={148}>
      {[0, 1, 2, 3].map((index) => (
        <LotInfoRowSkeleton
          key={index}
          labelWidth={index % 2 === 0 ? '40%' : '34%'}
          valueWidth={index % 2 === 0 ? '28%' : '36%'}
          hideDivider={index === 3}
        />
      ))}
    </LotInfoCollapseSectionSkeleton>

    <LotInfoCollapseSectionSkeleton titleWidth={128}>
      {[0, 1, 2].map((index) => (
        <LotInfoRowSkeleton
          key={index}
          labelWidth="36%"
          valueWidth="30%"
          hideDivider={index === 2}
        />
      ))}
    </LotInfoCollapseSectionSkeleton>
  </Box>
);

/** Skeleton de la lista de UF alineado a UnitListView. */
export const UnitListPanelSkeleton = () => (
  <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 2,
        py: 1,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}
    >
      <Skeleton
        variant="rounded"
        width={120}
        height={16}
        sx={{ bgcolor: 'rgba(255,255,255,0.12)', borderRadius: 0.5 }}
      />
      <Skeleton
        variant="rounded"
        width={28}
        height={28}
        sx={{ bgcolor: 'rgba(255,255,255,0.08)', borderRadius: '4px' }}
      />
    </Box>

    <Box
      sx={{
        flexShrink: 0,
        borderBottom: '1px solid rgba(255,255,255,0.06)',
        bgcolor: 'rgba(0,0,0,0.08)',
        px: 2,
        pt: 1.25,
        pb: 1.25,
      }}
    >
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Skeleton
          variant="rounded"
          width="50%"
          height={44}
          sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '8px' }}
        />
        <Skeleton
          variant="rounded"
          width="50%"
          height={44}
          sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '8px' }}
        />
      </Box>
    </Box>

    <Box>
      {[0, 1, 2].map((index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2,
            py: 1.5,
            borderBottom: '1px solid rgba(255,255,255,0.05)',
          }}
        >
          <Skeleton
            variant="rounded"
            width={36}
            height={36}
            sx={{
              bgcolor: 'rgba(255,255,255,0.06)',
              borderRadius: '8px',
              flexShrink: 0,
            }}
          />
          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            <Skeleton variant="text" width="58%" height={16} sx={{ bgcolor: 'rgba(255,255,255,0.12)' }} />
            <Skeleton variant="text" width="40%" height={12} sx={{ bgcolor: 'rgba(255,255,255,0.06)' }} />
          </Box>
          <Skeleton
            variant="rounded"
            width={16}
            height={16}
            sx={{ bgcolor: 'rgba(255,255,255,0.06)', borderRadius: 0.5, flexShrink: 0 }}
          />
        </Box>
      ))}
    </Box>
  </Box>
);

/** Cabecera de info privada con el mismo aspecto que LotInfoCollapseSection en el tab principal. */
export const LotUserPrivateInfoSection = ({ actions = null, loading = false, children }) => (
  <Box sx={{ mt: 2 }}>
    <LotInfoCollapseSection
      title="Información privada"
      collapsible={false}
      showCollapseToggle={false}
      actions={actions}
    >
      {loading ? (
        <LotUserPrivateInfoSkeleton />
      ) : children}
    </LotInfoCollapseSection>
  </Box>
);

/**
 * Scrollbar fina del Dock (la del cuerpo de tabs en DockPanel). Reusar en cualquier
 * nodo con overflowY dentro del Dock para no caer en la gris del navegador.
 */
export const DOCK_SCROLLBAR_SX = {
  scrollbarWidth: 'thin',
  scrollbarColor: 'rgba(255,255,255,0.22) transparent',
  '&::-webkit-scrollbar': { width: 6 },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(255,255,255,0.18)',
    borderRadius: 99,
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: 'rgba(255,255,255,0.18)',
  },
};
